import React, { useState } from "react";
import {
  Box,
  FormHelperText,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import EventFilledIcon from "@mui/icons-material/Event";
import { ControllerRenderProps } from "react-hook-form";
import { Moment } from "moment";
import moment from "moment";
import { dt } from "../../../themes/redesignTokens";

export interface FormDatePickerProps {
  // react-hook-form mode (use inside Controller render)
  field?: ControllerRenderProps<Record<string, unknown>, string>;
  // standalone mode
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  // common
  name?: string;
  label?: string;
  error?: string;
  disableFuture?: boolean;
  maxDate?: Moment;
  minDate?: Moment;
  format?: string;
  disabled?: boolean;
  /** Extra sx overrides for the calendar popper (e.g. accent color) */
  popperSx?: Record<string, unknown>;
}

/**
 * MUI DatePicker with dt styling, moment adapter, calendar icon button.
 *
 * Based on SR's DatePicker implementation (most complete).
 * Manages its own open/close calendar state internally.
 * Wraps LocalizationProvider + AdapterMoment so consumers don't need to.
 */
const FormDatePicker: React.FC<FormDatePickerProps> = ({
  field,
  value,
  onChange,
  name,
  error,
  disableFuture,
  maxDate,
  minDate,
  format = "DD.MM.YYYY",
  disabled,
  popperSx,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Resolve value: field mode vs standalone
  const resolvedValue = field
    ? moment(field.value as Date | string | undefined)
    : value
    ? moment(value)
    : null;

  const handleChange = (newValue: Moment | null) => {
    const dateValue = newValue?.toDate() || null;
    if (field) {
      field.onChange(dateValue);
    } else {
      onChange?.(dateValue);
    }
  };

  return (
    <Box>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePicker
          value={resolvedValue}
          onChange={handleChange}
          format={format}
          disableFuture={disableFuture}
          maxDate={maxDate}
          minDate={minDate}
          disabled={disabled}
          views={["year", "month", "day"]}
          open={isCalendarOpen}
          onOpen={() => setIsCalendarOpen(true)}
          onClose={() => setIsCalendarOpen(false)}
          slotProps={{
            textField: {
              size: "small",
              name: field?.name || name,
              fullWidth: true,
              error: !!error,
              sx: {
                "& .MuiOutlinedInput-root": {
                  fontFamily: dt.font,
                  fontSize: "14px",
                  borderRadius: dt.radiusSm,
                  backgroundColor: dt.inputBg,
                  "& fieldset": { borderColor: dt.cardBorder },
                  "&:hover fieldset": { borderColor: dt.blue },
                  "&.Mui-focused fieldset": { borderColor: dt.blue },
                },
              },
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setIsCalendarOpen(true)}
                      disabled={disabled}
                      sx={{
                        color: dt.blue,
                        "&:hover": { backgroundColor: dt.blueSoft },
                      }}
                    >
                      <EventFilledIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            },
            popper: {
              sx: {
                "& .MuiPickersDay-root.Mui-selected": {
                  backgroundColor: dt.blue,
                  "&:hover": { backgroundColor: dt.blue },
                },
                ...(popperSx || {}),
              },
            },
          }}
        />
      </LocalizationProvider>
      {error && (
        <FormHelperText error sx={{ mt: 0.5, fontFamily: dt.font }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

export default FormDatePicker;
